import {
  Breakpoint,
  Color,
  FontWeight,
  formatCurrency,
  Icon,
  IconName,
  Tooltip,
  TooltipPlacement,
  Typography,
  TypographyElement,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useContext, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { PageDataContext } from '../../components/page';
import { CalculationType } from '../../enums';
import { PageData } from '../../interfaces';
import { Calculation } from '../calculation/calculation.component';
import { CenteredContentSlideWrapper } from '../centeredContentSlideWrapper/centeredContentSlideWrapper.component';

import { inflationBonus, month, unformattedCompensationValue, unformattedVehicleValue } from './additionalProfitsCalculator.const';
import { CalculationData, Container, IconWrapper, List, StyledMarkdown } from './additionalProfitsCalculator.styled';
import { AdditionalProfitsCalculatorProps, CalcState } from './additionalProfitsCalculator.types';

export const AdditionalProfitsCalculator: React.FC<AdditionalProfitsCalculatorProps> = (
  props: AdditionalProfitsCalculatorProps
): JSX.Element => {
  const { callComplete, completed, centeredContentSlideWrapperData, baseUrl }: AdditionalProfitsCalculatorProps = props;
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const {
    slidesOverallCounter,
    currentSlideCounter,
    title,
    description,
  }: Partial<PageData> = dataCtx ?? {};
  const unformattedGapRtiValue: number = unformattedVehicleValue - unformattedCompensationValue;
  const vehicleValuePercent: number = month * inflationBonus;
  const unformattedIndexationValue: number = unformattedVehicleValue * vehicleValuePercent / 100;
  const unformattedProfit: number = unformattedGapRtiValue + unformattedIndexationValue;

  const vehicleValue: string = formatCurrency(unformattedVehicleValue, undefined, undefined, true);
  const compensationValue: string = formatCurrency(unformattedCompensationValue, undefined, undefined, true);
  const gapRtiValue: string = formatCurrency(unformattedGapRtiValue, undefined, undefined, true);
  const indexationValue: string = formatCurrency(unformattedIndexationValue, undefined, undefined, true);
  const profit: string = formatCurrency(unformattedProfit, undefined, undefined, true);

  const { t }: TransProps<never> = useTranslation();
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const [calcState, setCalcState]: UseState<CalcState> = useState<CalcState>({
    isFirstCalcSolved: false,
    isSecondCalcSolved: false,
    isThirdCalcSolved: false,
  });

  const handleCallComplete: (solved: boolean) => void = (solved: boolean): void => {
    if (solved && !completed) {
      callComplete();
    }
  };

  return (
    <CenteredContentSlideWrapper
      currentSlideCounter={currentSlideCounter}
      description={description}
      leftImageSrc={baseUrl + centeredContentSlideWrapperData.leftImageSrc}
      rightImageSrc={baseUrl + centeredContentSlideWrapperData.rightImageSrc}
      slidesOverallCounter={slidesOverallCounter}
      title={title}
    >
      <Container>
        <CalculationData>
          <Typography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
            {t('competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise1.title')}
          </Typography>
          <List>
            <Typography tag={TypographyElement.span} variant={TypographyVariant.TextMD}>
              <StyledMarkdown text={t(
                'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise1.info1',
                { vehicleValue }
              )}
              />
            </Typography>
            <Typography tag={TypographyElement.span} variant={TypographyVariant.TextMD}>
              <StyledMarkdown text={t(
                'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise1.info2',
                { compensationValue }
              )}
              />
            </Typography>
          </List>
        </CalculationData>
        <Calculation
          answer={unformattedVehicleValue - unformattedCompensationValue}
          calculationType={CalculationType.Substraction}
          correctAnswerText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise1.correctAnswerText',
            { gapRtiValue }
          )}
          correctionText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise1.correctionText',
            { vehicleValue, compensationValue, gapRtiValue }
          )}
          firstFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.vehicleBaseValue',
            unit: 'zł',
          }}
          onSolvedStateChange={(solved: boolean): void =>
            setCalcState((prev: CalcState): CalcState => ({ ...prev, isFirstCalcSolved: solved }))
          }
          resultFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.gapRtiValue',
            unit: 'zł',
          }}
          secondFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.compensationValue',
            unit: 'zł',
          }}
        />
        <CalculationData>
          <Typography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
            {t('competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise2.title')}
            <Tooltip
              leaveTouchDelay={6000}
              placement={isSmallDesktop ? TooltipPlacement.Right : TooltipPlacement.Bottom}
              title={t('competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.tooltipText')}
            >
              <IconWrapper>
                <Icon color={Color.Brand300} name={IconName.Info} size={24} />
              </IconWrapper>
            </Tooltip>
          </Typography>
          <List>
            <Typography tag={TypographyElement.span} variant={TypographyVariant.TextMD}>
              <StyledMarkdown text={t(
                'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise2.info1',
                { vehicleValue }
              )}
              />
            </Typography>
            <Typography tag={TypographyElement.span} variant={TypographyVariant.TextMD}>
              <StyledMarkdown text={t(
                'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise2.info2',
                { month }
              )}
              />
            </Typography>
            <Typography tag={TypographyElement.span} variant={TypographyVariant.TextMD}>
              <StyledMarkdown text={t(
                'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise2.info3',
                { inflationBonus }
              )}
              />
            </Typography>
          </List>
        </CalculationData>
        <Calculation
          answer={vehicleValuePercent}
          calculationType={CalculationType.Multiplication}
          correctAnswerText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise2.correctAnswerText',
            { vehicleValuePercent }
          )}
          correctionText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise2.correctionText',
            { month, inflationBonus, vehicleValuePercent }
          )}
          firstFieldData={{ label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.month' }}
          isDisabled={!calcState.isFirstCalcSolved}
          onSolvedStateChange={(solved: boolean): void =>
            setCalcState((prev: CalcState): CalcState => ({ ...prev, isSecondCalcSolved: solved }))
          }
          resultFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.vehicleValuePercent',
            unit: '%',
          }}
          secondFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.inflationBonus',
            unit: '%',
          }}
        />
        <Calculation
          answer={unformattedIndexationValue}
          calculationType={CalculationType.Multiplication}
          correctAnswerText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise3.correctAnswerText',
            { indexationValue }
          )}
          correctionText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise3.correctionText',
            { vehicleValue, vehicleValuePercent, indexationValue }
          )}
          firstFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.vehicleBaseValue',
            unit: 'zł',
          }}
          isDisabled={!calcState.isSecondCalcSolved}
          onSolvedStateChange={(solved: boolean): void =>
            setCalcState((prev: CalcState): CalcState => ({ ...prev, isThirdCalcSolved: solved }))
          }
          resultFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.indexationValue', unit: 'zł' }}
          secondFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.vehicleValuePercent',
            unit: '%',
            isPercentValue: true,
          }}
        />
        <Typography fontWeight={FontWeight.SemiBold} tag={TypographyElement.span} variant={TypographyVariant.TextLG}>
          {t('competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise3.title')}
          <Tooltip
            leaveTouchDelay={6000}
            placement={isSmallDesktop ? TooltipPlacement.Right : TooltipPlacement.Bottom}
            title={t('competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.tooltipText')}
          >
            <IconWrapper>
              <Icon color={Color.Brand300} name={IconName.Info} size={24} />
            </IconWrapper>
          </Tooltip>
        </Typography>
        <Calculation
          answer={unformattedProfit}
          calculationType={CalculationType.Addition}
          correctAnswerText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise4.correctAnswerText',
            { profit }
          )}
          correctionText={t(
            'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.exercise4.correctionText',
            { gapRtiValue, indexationValue, profit }
          )}
          firstFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.gapRtiValue',
            unit: 'zł',
          }}
          isDisabled={!calcState.isThirdCalcSolved}
          onSolvedStateChange={handleCallComplete}
          resultFieldData={{ label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.profit', unit: 'zł' }}
          secondFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.additionalProfitsCalculator.inputLabel.indexationValue',
            unit: 'zł',
          }}
        />
      </Container>
    </CenteredContentSlideWrapper>
  );
};
